import React, {useEffect, useRef, useState} from "react";
import {useParams, useNavigate, Link} from "react-router-dom";
import {getProduct, getProducts} from "../api";
import {useCart, withColor} from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import ProductReviews from "../components/ProductReviews";
import StarRating from "../components/StarRating";
import "./ProductDetail.css";

const DESCRIPTION_LIMIT = 260;

const TRUST_ITEMS = [
  "Precision 3D printed",
  "Eco-conscious PLA",
  "Finished by hand",
  "Loved nationwide",
];

const ACCORDIONS = [
  {
    title: "Shipping & Delivery",
    body: "Free shipping on all orders above ₹699, 10% off on orders above ₹1,999, and a surprise gift on orders above ₹3,999. Every piece is made to order and dispatched within 3–7 business days, with delivery typically taking a further 2–5 days depending on your location.",
  },
  {
    title: "Materials & Care",
    body: "Printed in premium PLA — a durable, eco-friendly plastic with a smooth matte finish. Wipe clean with a soft, dry cloth and keep out of direct sunlight or high heat for lasting colour.",
  },
  {
    title: "Returns & Exchanges",
    body: "As every item is made specifically for you, we accept returns only for pieces that arrive damaged, defective, or incorrect. Reach out within 48 hours of delivery and we'll make it right.",
  },
];

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function formatDate(date) {
  return date.toLocaleDateString("en-IN", {month: "short", day: "numeric"});
}

function useDeliveryEstimate() {
  const today = new Date();
  const dispatchStart = addDays(today, 2);
  const dispatchEnd = addDays(today, 3);
  const deliveryStart = addDays(dispatchStart, 3);
  const deliveryEnd = addDays(dispatchEnd, 4);
  return {
    ordered: formatDate(today),
    processing: `${formatDate(dispatchStart)} – ${formatDate(dispatchEnd)}`,
    delivered: `${formatDate(deliveryStart)} – ${formatDate(deliveryEnd)}`,
  };
}

export default function ProductDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {addToCart} = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [customNames, setCustomNames] = useState([""]);
  const [customError, setCustomError] = useState("");
  const [selectedColor, setSelectedColor] = useState(null);
  const [slideDir, setSlideDir] = useState("");
  const [related, setRelated] = useState([]);
  const [descExpanded, setDescExpanded] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(0);
  const [reviewFormRequest, setReviewFormRequest] = useState(0);

  const estimate = useDeliveryEstimate();

  // Swipe tracking for the main image
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const mainImgRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setRelated([]);
    setActiveImage(0);
    setQty(1);
    setCustomNames([""]);
    setCustomError("");
    setDescExpanded(false);
    getProduct(id)
      .then((r) => {
        setProduct(r.data);
        setSelectedColor(r.data.colors?.[0] || null);
        setLoading(false);
        window.scrollTo(0, 0);
      })
      .catch(() => {
        setLoading(false);
        navigate("/products");
      });
  }, [id, navigate]);

  // "You may also like" — same-category picks, falling back to newest products
  useEffect(() => {
    if (!product) return;
    const categoryId = product.category?._id || product.category;
    getProducts(categoryId ? {category: categoryId, limit: 9} : {limit: 9})
      .then((r) => {
        const currentId = product._id;
        let picks = (r.data.products || []).filter((p) => p._id !== currentId);
        if (picks.length === 0 && categoryId) {
          return getProducts({limit: 9}).then((r2) =>
            setRelated(
              (r2.data.products || [])
                .filter((p) => p._id !== currentId)
                .slice(0, 8),
            ),
          );
        }
        setRelated(picks.slice(0, 8));
      })
      .catch(() => {});
  }, [product]);

  // One name per unit — keep the inputs in step with the quantity
  function changeQty(next) {
    setQty(next);
    setCustomNames((names) =>
      Array.from({length: next}, (_, i) => names[i] || ""),
    );
    setCustomError("");
  }

  // Returns false (and shows an error) if any customised name is invalid
  function addAllToCart() {
    if (product.isCustomizable) {
      const {customMinLength: min, customMaxLength: max} = product;
      const names = customNames.map((n) => n.trim());
      if (names.some((n) => n.length < min || n.length > max)) {
        setCustomError(
          qty > 1
            ? `Enter a name of ${min}-${max} characters for every item.`
            : `Enter a name of ${min}-${max} characters.`,
        );
        return false;
      }
      names.forEach((customName) =>
        addToCart({...withColor(product, selectedColor), customName}),
      );
      setCustomNames(Array.from({length: qty}, () => ""));
      return true;
    }
    for (let i = 0; i < qty; i++) addToCart(withColor(product, selectedColor));
    return true;
  }

  function handleAddToCart() {
    if (!addAllToCart()) return;
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  }

  function handleBuyNow() {
    if (addAllToCart()) navigate("/checkout");
  }

  if (loading) return <div className="pdp-loading">Loading…</div>;
  if (!product) return null;

  const discount =
    product.originalPrice && product.originalPrice > product.price
      ? Math.round(
          ((product.originalPrice - product.price) / product.originalPrice) *
            100,
        )
      : null;

  // Gallery: the selected colour's image, then product images, then videos
  const images = [
    ...(selectedColor?.image?.url
      ? [{type: "image", url: selectedColor.image.url}]
      : []),
    ...(product.images || []).map((img) => ({type: "image", url: img.url})),
    ...(product.videos || []).map((v) => ({type: "video", url: v.url})),
  ];
  if (images.length === 0)
    images.push({
      type: "image",
      url: "https://placehold.co/600x600/f5f5f5/aaa?text=Product",
    });
  const activeMedia = images[activeImage] || images[0];
  const isVideoActive = activeMedia.type === "video";

  const isLongDescription =
    (product.description?.length || 0) > DESCRIPTION_LIMIT;
  const descriptionText =
    isLongDescription && !descExpanded
      ? product.description.slice(0, DESCRIPTION_LIMIT).trim() + "…"
      : product.description;

  function scrollToReviews(e) {
    e.preventDefault();
    if (product.reviewCount > 0) {
      document.getElementById("reviews")?.scrollIntoView({behavior: "smooth"});
    } else {
      setReviewFormRequest((n) => n + 1); // jump straight to the review form
    }
  }

  function showPrevImage() {
    setSlideDir("right");
    setActiveImage((i) => (i - 1 + images.length) % images.length);
  }

  function showNextImage() {
    setSlideDir("left");
    setActiveImage((i) => (i + 1) % images.length);
  }

  function resetDragStyle() {
    const el = mainImgRef.current;
    if (!el) return;
    el.style.transition = "transform .25s ease, opacity .25s ease";
    el.style.transform = "";
    el.style.opacity = "";
  }

  function handlePointerDown(e) {
    touchStartX.current = e.clientX;
    touchDeltaX.current = 0;
    if (mainImgRef.current) mainImgRef.current.style.transition = "none";
  }

  function handlePointerMove(e) {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.clientX - touchStartX.current;
    const el = mainImgRef.current;
    if (el && images.length > 1) {
      const shift = touchDeltaX.current * 0.6; // dampen so it feels tethered
      el.style.transform = `translateX(${shift}px)`;
      el.style.opacity = String(1 - Math.min(Math.abs(shift) / 260, 0.4));
    }
  }

  function handlePointerEnd() {
    const SWIPE_THRESHOLD = 50; // px
    const delta = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;

    if (images.length > 1 && Math.abs(delta) > SWIPE_THRESHOLD) {
      if (delta < 0) showNextImage();
      else showPrevImage();
    } else {
      resetDragStyle(); // snap back to center
    }
  }

  return (
    <div className="pdp">
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <Link to="/">Home</Link>
        <span className="crumb-sep">/</span>
        <Link to="/products">Products</Link>
        <span className="crumb-sep">/</span>
        <span className="current">{product.name}</span>
      </nav>

      <div className="pdp-layout">
        {/* Images */}
        <div className="pdp-images">
          <div className="pdp-gallery">
            {images.length > 1 && (
              <div className="pdp-thumbs">
                {images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`pdp-thumb-btn ${activeImage === i ? "active" : ""}`}
                    onClick={() => setActiveImage(i)}
                    aria-label={img.type === "video" ? `Play video` : `View ${i + 1}`}
                  >
                    {img.type === "video" ? (
                      <>
                        {/* #t=0.1 makes browsers paint a frame as the thumbnail */}
                        <video
                          src={`${img.url}#t=0.1`}
                          className="pdp-thumb"
                          preload="metadata"
                          muted
                          playsInline
                        />
                        <span className="pdp-thumb-play" aria-hidden="true">
                          ▶
                        </span>
                      </>
                    ) : (
                      <img
                        src={img.url}
                        alt={`View ${i + 1}`}
                        className="pdp-thumb"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}

            <div
              className={`pdp-main-image ${isVideoActive ? "is-video" : ""}`}
              // No swipe on videos: dragging would fight the video controls
              onPointerDown={isVideoActive ? undefined : handlePointerDown}
              onPointerMove={isVideoActive ? undefined : handlePointerMove}
              onPointerUp={isVideoActive ? undefined : handlePointerEnd}
              onPointerLeave={isVideoActive ? undefined : handlePointerEnd}
            >
              {isVideoActive ? (
                <video
                  key={activeImage}
                  src={activeMedia.url}
                  className={`pdp-main-video slide-${slideDir}`}
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  key={activeImage}
                  ref={mainImgRef}
                  src={activeMedia.url}
                  alt={product.name}
                  draggable={false}
                  className={`pdp-main-img slide-${slideDir}`}
                />
              )}
              {discount && (
                <span className="pdp-tag-sale">{discount}% Off</span>
              )}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="pdp-nav prev"
                    aria-label="Previous image"
                    onClick={showPrevImage}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="pdp-nav next"
                    aria-label="Next image"
                    onClick={showNextImage}
                  >
                    ›
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="pdp-info">
          {product.category && (
            <p className="pdp-category">{product.category.name}</p>
          )}
          <h1 className="pdp-title">{product.name}</h1>

          <a href="#reviews" className="pdp-rating" onClick={scrollToReviews}>
            {product.reviewCount > 0 ? (
              <>
                <StarRating value={product.rating} size={16} />
                <span className="pdp-rating-value">
                  {product.rating.toFixed(1)}
                </span>
                <span className="pdp-review-count">
                  {product.reviewCount} review
                  {product.reviewCount === 1 ? "" : "s"}
                </span>
              </>
            ) : (
              <>
                <StarRating value={0} size={16} />
                <span className="pdp-review-count first">
                  Be the first to review
                </span>
              </>
            )}
          </a>

          <div className="pdp-price-row">
            <span className="pdp-price">
              ₹{product.price.toLocaleString("en-IN")}
            </span>
            {product.originalPrice && (
              <>
                <span className="pdp-original">
                  ₹{product.originalPrice.toLocaleString("en-IN")}
                </span>
                <span className="pdp-discount-label">{discount}% off</span>
              </>
            )}
          </div>

          <p className="pdp-description">
            {descriptionText}
            {isLongDescription && (
              <button
                type="button"
                className="pdp-desc-toggle"
                onClick={() => setDescExpanded((v) => !v)}
              >
                {descExpanded ? "Show less" : "Read more"}
              </button>
            )}
          </p>

          {/* Tags */}
          {product.tags?.length > 0 && (
            <div className="pdp-tags">
              {product.tags.map((t) => (
                <span key={t} className="pdp-tag">
                  {t}
                </span>
              ))}
            </div>
          )}

          <div className="pdp-divider" />

          {/* Colour options — only for products that have them */}
          {product.colors?.length > 0 && (
            <div className="pdp-colors">
              <p className="pdp-colors-label">
                Colour:{" "}
                <span>{selectedColor?.name || selectedColor?.hex}</span>
              </p>
              <div className="pdp-swatches">
                {product.colors.map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    className={`pdp-swatch ${selectedColor?.hex === c.hex ? "active" : ""}`}
                    style={{background: c.hex}}
                    title={c.name || c.hex}
                    aria-label={c.name || c.hex}
                    aria-pressed={selectedColor?.hex === c.hex}
                    onClick={() => {
                      setSelectedColor(c);
                      setActiveImage(0); // show this colour's image
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Quantity + Add to Cart */}
          <div className="pdp-qty-row">
            <span className="pdp-qty-label">Quantity</span>
            <div className="qty-control">
              <button onClick={() => changeQty(Math.max(1, qty - 1))}>
                −
              </button>
              <span>{qty}</span>
              <button
                onClick={() => changeQty(Math.min(product.stock, qty + 1))}
              >
                +
              </button>
            </div>
            <span
              className={`pdp-stock ${product.stock > 0 && product.stock <= 10 ? "low" : ""}`}
            >
              <span className="pdp-stock-dot" />
              {product.stock > 10
                ? "In stock"
                : product.stock > 0
                  ? `Only ${product.stock} left`
                  : "Out of stock"}
            </span>
          </div>

          {/* Personalisation — one name per item */}
          {product.isCustomizable && (
            <div className="pdp-custom">
              <p className="pdp-custom-label">
                Personalise{qty > 1 ? " each item" : ""}{" "}
                <span>
                  ({product.customMinLength}-{product.customMaxLength}{" "}
                  characters)
                </span>
              </p>
              {customNames.map((name, i) => (
                <input
                  key={i}
                  className="pdp-custom-input"
                  value={name}
                  maxLength={product.customMaxLength}
                  placeholder={qty > 1 ? `Name for item ${i + 1}` : "Enter name"}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCustomNames((names) =>
                      names.map((n, j) => (j === i ? v : n)),
                    );
                    setCustomError("");
                  }}
                />
              ))}
              {customError && <p className="pdp-custom-error">{customError}</p>}
            </div>
          )}

          <div className="pdp-actions">
            <button
              className={`btn-pill outline pdp-btn-atc ${added ? "added" : ""}`}
              onClick={handleAddToCart}
              disabled={product.stock === 0}
            >
              {added ? "Added ✓" : "Add to Cart"}
            </button>
          </div>
          <button
            className="btn-pill solid pdp-btn-buy"
            onClick={handleBuyNow}
            disabled={product.stock === 0}
          >
            Buy it Now
          </button>

          {/* Delivery timeline */}
          <div className="pdp-timeline">
            <div className="pdp-timeline-step done">
              <span className="pdp-timeline-dot" />
              <span className="pdp-timeline-label">Ordered</span>
              <span className="pdp-timeline-date">{estimate.ordered}</span>
            </div>
            <div className="pdp-timeline-track" />
            <div className="pdp-timeline-step">
              <span className="pdp-timeline-dot" />
              <span className="pdp-timeline-label">Processing</span>
              <span className="pdp-timeline-date">{estimate.processing}</span>
            </div>
            <div className="pdp-timeline-track" />
            <div className="pdp-timeline-step">
              <span className="pdp-timeline-dot" />
              <span className="pdp-timeline-label">Delivered</span>
              <span className="pdp-timeline-date">{estimate.delivered}</span>
            </div>
          </div>

          {/* Accordions */}
          <div className="pdp-accordions">
            {ACCORDIONS.map((a, i) => (
              <div className="pdp-accordion" key={a.title}>
                <button
                  type="button"
                  className="pdp-accordion-head"
                  onClick={() => setOpenAccordion(openAccordion === i ? -1 : i)}
                >
                  {a.title}
                  <span className="pdp-accordion-icon">
                    {openAccordion === i ? "−" : "+"}
                  </span>
                </button>
                {openAccordion === i && (
                  <p className="pdp-accordion-body">{a.body}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="pdp-trust-strip">
        {TRUST_ITEMS.map((t, i) => (
          <React.Fragment key={t}>
            {i > 0 && <span className="pdp-trust-sep">·</span>}
            <span>{t}</span>
          </React.Fragment>
        ))}
      </div>

      {/* Ratings & reviews */}
      <ProductReviews
        key={product._id}
        productId={product._id}
        openRequest={reviewFormRequest}
      />

      {/* You may also like */}
      {related.length > 0 && (
        <section className="pdp-related">
          <div className="pdp-related-header">
            <p className="pdp-related-eyebrow">Complete the look</p>
            <h2 className="pdp-related-title">You May Also Like</h2>
          </div>
          <div className="pdp-related-grid">
            {related.map((p) => (
              <ProductCard key={p._id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
